import { useState, useCallback, useEffect, useRef } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    TextField,
    Button,
    Typography,
    Stack,
    Autocomplete,
    CircularProgress,
    Grid,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import axios from 'axios';

const AddSaleInvoicePrevFYModal = ({
    open,
    onClose,
    onSave,
    formData,
    onFormChange,
    onAmountChange,
    connectionString,
    companyId,
    userId,
    userRole,
    title = 'Add Sale Invoice (Previous FY)',
}) => {
    const [clientOptions, setClientOptions] = useState([]);
    const [clientLoading, setClientLoading] = useState(false);
    const [searchInputValue, setSearchInputValue] = useState('');
    const [selectedPartyId, setSelectedPartyId] = useState(null);
    const isFormValid = formData.invoiceDate && formData.amount && formData.partyName && formData.invoiceNo;
    const API_URL = `${connectionString}/api`;
    const searchTimeoutRef = useRef(null);
    const partyNameInputRef = useRef(null);

    // Get current and previous financial year range
    const getPreviousFYRange = () => {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1; // 0-based to 1-based

        let currentFYStartYear, currentFYEndYear;
        if (currentMonth >= 4) {
            currentFYStartYear = currentYear;
            currentFYEndYear = currentYear + 1;
        } else {
            currentFYStartYear = currentYear - 1;
            currentFYEndYear = currentYear;
        }

        // Previous FY is one year before current FY
        const prevFYStartYear = currentFYStartYear - 1;
        const prevFYEndYear = currentFYEndYear - 1;

        const prevFYStartDate = `${prevFYStartYear}-04-01`;
        const prevFYEndDate = `${prevFYEndYear}-03-31`;

        return {
            prevFYStartDate,
            prevFYEndDate,
            prevFYStartYear,
            prevFYEndYear,
            currentFYStartYear,
            currentFYEndYear
        };
    };

    // Convert date format: yyyy-mm-dd to dd-mm-yyyy
    const formatDateDisplay = (dateStr) => {
        if (!dateStr) return '';
        if (dateStr.includes('-') && dateStr.length === 10) {
            const [yyyy, mm, dd] = dateStr.split('-');
            return `${dd}-${mm}-${yyyy}`;
        }
        return dateStr;
    };

    // Convert date format: dd-mm-yyyy to yyyy-mm-dd
    const formatDateInternal = (dateStr) => {
        if (!dateStr) return '';

        // Remove any extra spaces
        const cleaned = dateStr.trim();

        // Check if already in yyyy-mm-dd format
        if (cleaned.match(/^\d{4}-\d{2}-\d{2}$/)) {
            return cleaned;
        }

        // Try to parse dd-mm-yyyy format
        const parts = cleaned.split('-');
        if (parts.length === 3) {
            const [dd, mm, yyyy] = parts;
            if (yyyy && yyyy.length === 4 && mm && dd) {
                // Validate day and month
                const day = parseInt(dd, 10);
                const month = parseInt(mm, 10);
                if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
                    return `${yyyy}-${mm}-${dd}`;
                }
            }
        }
        return '';
    };

    const searchClients = useCallback(async (searchQuery) => {
        if (!searchQuery || searchQuery.length < 1) {
            setClientOptions([]);
            return;
        }

        setClientLoading(true);
        try {
            const response = await axios.get(`${API_URL}/company-search`, {
                params: {
                    field: 'FirmName',
                    search: searchQuery,
                    companyId,
                    page: 1,
                    limit: 10,
                }
            });

            // Handle different response structures
            let clients = [];
            if (Array.isArray(response.data)) {
                clients = response.data;
            } else if (response.data?.data && Array.isArray(response.data.data)) {
                clients = response.data.data;
            } else if (response.data?.results && Array.isArray(response.data.results)) {
                clients = response.data.results;
            }

            setClientOptions(clients);
        } catch (error) {
            console.error('Error searching clients:', error);
            setClientOptions([]);
        } finally {
            setClientLoading(false);
        }
    }, [API_URL, companyId]);

    const handleClientChange = (_, value, reason) => {
        if (value) {
            onFormChange('partyName', value.FirmName);
            setSelectedPartyId(value.id);
            setSearchInputValue(value.FirmName);
        } else if (reason === 'clear') {
            // Only treat an explicit clear (the little 'x' button) as the user
            // wanting to remove the party. On blur with unmatched typed text,
            // MUI also calls onChange(null) with reason 'blur' — without this
            // guard that wiped out a pre-filled edit-mode party name (whose
            // FirmName never matched any option because clientOptions stays
            // empty until the user actually searches) the instant focus left.
            onFormChange('partyName', '');
            setSelectedPartyId(null);
            setSearchInputValue('');
        }
    };

    // Focus Party Name whenever the modal opens (this modal is always an
    // "add" action, so it always focuses on open — no edit mode to gate on).
    useEffect(() => {
        if (open) {
            const timeoutId = setTimeout(() => partyNameInputRef.current?.focus(), 50);
            return () => clearTimeout(timeoutId);
        }
    }, [open]);

    // Sync the visible search text with formData.partyName whenever the modal
    // opens already pre-filled (edit mode). Without this, the Autocomplete's
    // inputValue stays tied to clientOptions (empty until the user types a
    // search), so a pre-filled party name shows only while focused and then
    // disappears on blur once MUI recalculates the display from an unmatched value.
    useEffect(() => {
        if (open && formData.partyName) {
            setSearchInputValue(formData.partyName);
        }
    }, [open, formData.partyName]);

    const handleSaveClick = () => {
        // Pass selectedPartyId directly instead of relying on onFormChange having
        // already propagated into the parent's formData by the time onSave reads
        // it — setState is async, so calling onSave() right after onFormChange()
        // would otherwise see the previous (stale) partyId on the first click.
        onFormChange('partyId', selectedPartyId);
        onSave(selectedPartyId);
    };

    // ──────────────── Alt+S (Save) & Alt+C (Cancel/Close) shortcuts ───────────
    // Guarded by `open`: this modal component stays mounted in its parent even
    // when closed, so without this guard the listener would keep intercepting
    // Alt+S/Alt+C everywhere in the app, not just while this dialog is showing.
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    useEffect(() => {
        if (!open) return;

        const handleGlobalKeyDown = (e) => {
            if (!e.altKey) return;

            if (e.code === "KeyS") {
                e.preventDefault();
                if (isFormValid) {
                    handleSaveClick();
                }
            } else if (e.code === "KeyC") {
                e.preventDefault();
                onClose();
            }
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    });

    return (
        <Dialog
            open={open}
            onClose={(_, reason) => {
                // Only allow closing via the Close button, not by pressing Escape or clicking backdrop
                if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
                    return;
                }
            }}
            disableEscapeKeyDown
            maxWidth="sm"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: '12px',
                    boxShadow: '0 10px 40px rgba(24, 119, 242, 0.15)',
                }
            }}
        >
            {/* Header */}
            <DialogTitle
                sx={{
                    background: 'linear-gradient(135deg, #1877F2 0%, #0A66C2 100%)',
                    color: '#FFFFFF',
                    fontWeight: 700,
                    fontSize: '1.2rem',
                    py: 2.5,
                    px: 3,
                }}
            >
                {title}
            </DialogTitle>

            {/* Content */}
            <DialogContent
                sx={{
                    pt: 3,
                    pb: 2.5,
                    px: 3,
                    backgroundColor: '#FFFFFF',
                }}
            >
                <Stack spacing={2.8}>
                    {/* Invoice Date */}
                    <Box>
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ mb: 0.8, color: '#1F2937', fontSize: '0.9rem' }}
                        >
                            Invoice Date <span style={{ color: '#1877F2', fontWeight: 800 }}>*</span>
                        </Typography>
                        <TextField
                            fullWidth
                            type="date"
                            size="small"
                            value={formData.invoiceDate}
                            onChange={(e) => onFormChange('invoiceDate', e.target.value)}
                            inputProps={{
                                max: getPreviousFYRange().prevFYEndDate,
                            }}
                            InputLabelProps={{
                                shrink: true,
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#F5F5F5',
                                    border: '1px solid #E5E7EB',
                                    '&:hover': {
                                        backgroundColor: '#F9F9F9',
                                        borderColor: '#1877F2',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#FFFFFF',
                                        borderColor: '#1877F2',
                                        boxShadow: '0 0 0 3px rgba(24, 119, 242, 0.1)',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    fontSize: '0.95rem',
                                    color: '#1F2937',
                                },
                                '& .MuiOutlinedInput-input::placeholder': {
                                    color: '#9CA3AF',
                                    opacity: 1,
                                },
                            }}
                        />
                    </Box>

                    {/* Party Name with Search */}
                    <Box>
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ mb: 0.8, color: '#1F2937', fontSize: '0.9rem' }}
                        >
                            Party Name <span style={{ color: '#1877F2', fontWeight: 800 }}>*</span>
                        </Typography>
                        <Autocomplete
                            fullWidth
                            size="small"
                            options={clientOptions}
                            getOptionLabel={(option) => option?.FirmName || ''}
                            inputValue={searchInputValue}
                            onInputChange={(_, value) => {
                                setSearchInputValue(value);
                                if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
                                if (value && value.length > 0) {
                                    // Debounced 350ms so a fast typist doesn't fire one API
                                    // call per keystroke — search still starts automatically
                                    // as the user types, just waits for a brief pause.
                                    searchTimeoutRef.current = setTimeout(() => {
                                        searchClients(value);
                                    }, 350);
                                } else {
                                    setClientOptions([]);
                                }
                            }}
                            onChange={handleClientChange}
                            value={clientOptions.find(c => c?.FirmName === formData.partyName) || null}
                            loading={clientLoading}
                            noOptionsText="No clients found. Type to search..."
                            filterOptions={(x) => x}
                            isOptionEqualToValue={(option, value) => option?.id === value?.id}
                            slotProps={{
                                paper: {
                                    sx: {
                                        borderRadius: '8px',
                                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
                                    }
                                }
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputRef={partyNameInputRef}
                                    autoFocus
                                    placeholder="Type to search clients..."
                                    size="small"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {clientLoading ? <CircularProgress color="inherit" size={18} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            backgroundColor: '#F5F5F5',
                                            border: '1px solid #E5E7EB',
                                            '&:hover': {
                                                backgroundColor: '#F9F9F9',
                                                borderColor: '#1877F2',
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: '#FFFFFF',
                                                borderColor: '#1877F2',
                                                boxShadow: '0 0 0 3px rgba(24, 119, 242, 0.1)',
                                            },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            fontSize: '0.95rem',
                                            color: '#1F2937',
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>

                    {/* Amount and Invoice No Row */}
                    <Grid container spacing={2}>
                        {/* Amount - 60% */}
                        <Grid item xs={12} sm={7.2}>
                            <Box>
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{ mb: 0.8, color: '#1F2937', fontSize: '0.9rem' }}
                                >
                                    Amount <span style={{ color: '#1877F2', fontWeight: 800 }}>*</span>
                                </Typography>
                                <TextField
                                    fullWidth
                                    type="text"
                                    size="small"
                                    value={formData.amount}
                                    onChange={(e) => onAmountChange(e.target.value)}
                                    placeholder="0.00"
                                    inputProps={{
                                        inputMode: 'decimal',
                                        pattern: '^[0-9]*.?[0-9]{0,2}$'
                                    }}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            backgroundColor: '#F5F5F5',
                                            border: '1px solid #E5E7EB',
                                            '&:hover': {
                                                backgroundColor: '#F9F9F9',
                                                borderColor: '#1877F2',
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: '#FFFFFF',
                                                borderColor: '#1877F2',
                                                boxShadow: '0 0 0 3px rgba(24, 119, 242, 0.1)',
                                            },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            fontSize: '0.95rem',
                                            color: '#1F2937',
                                            fontWeight: 600,
                                        },
                                    }}
                                />
                            </Box>
                        </Grid>

                        {/* Invoice No - 40% */}
                        <Grid item xs={12} sm={4.8}>
                            <Box>
                                <Typography
                                    variant="body2"
                                    fontWeight={600}
                                    sx={{ mb: 0.8, color: '#1F2937', fontSize: '0.9rem' }}
                                >
                                    Invoice No <span style={{ color: '#1877F2', fontWeight: 800 }}>*</span>
                                </Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    value={formData.invoiceNo}
                                    onChange={(e) => onFormChange('invoiceNo', e.target.value)}
                                    placeholder="SI-001"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: '8px',
                                            backgroundColor: '#F5F5F5',
                                            border: '1px solid #E5E7EB',
                                            '&:hover': {
                                                backgroundColor: '#F9F9F9',
                                                borderColor: '#1877F2',
                                            },
                                            '&.Mui-focused': {
                                                backgroundColor: '#FFFFFF',
                                                borderColor: '#1877F2',
                                                boxShadow: '0 0 0 3px rgba(24, 119, 242, 0.1)',
                                            },
                                        },
                                        '& .MuiOutlinedInput-input': {
                                            fontSize: '0.95rem',
                                            color: '#1F2937',
                                        },
                                    }}
                                />
                            </Box>
                        </Grid>
                    </Grid>

                    {/* Agent Name */}
                    <Box>
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ mb: 0.8, color: '#1F2937', fontSize: '0.9rem' }}
                        >
                            Agent Name
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            value={formData.agentName}
                            onChange={(e) => onFormChange('agentName', e.target.value)}
                            placeholder="Enter agent name"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#F5F5F5',
                                    border: '1px solid #E5E7EB',
                                    '&:hover': {
                                        backgroundColor: '#F9F9F9',
                                        borderColor: '#1877F2',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#FFFFFF',
                                        borderColor: '#1877F2',
                                        boxShadow: '0 0 0 3px rgba(24, 119, 242, 0.1)',
                                    },
                                },
                                '& .MuiOutlinedInput-input': {
                                    fontSize: '0.95rem',
                                    color: '#1F2937',
                                },
                            }}
                        />
                    </Box>

                    {/* Remark */}
                    <Box>
                        <Typography
                            variant="body2"
                            fontWeight={600}
                            sx={{ mb: 0.8, color: '#1F2937', fontSize: '0.9rem' }}
                        >
                            Remark
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={2}
                            size="small"
                            value={formData.remark}
                            onChange={(e) => onFormChange('remark', e.target.value)}
                            placeholder="Enter remarks"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                    backgroundColor: '#F5F5F5',
                                    border: '1px solid #E5E7EB',
                                    '&:hover': {
                                        backgroundColor: '#F9F9F9',
                                        borderColor: '#1877F2',
                                    },
                                    '&.Mui-focused': {
                                        backgroundColor: '#FFFFFF',
                                        borderColor: '#1877F2',
                                        boxShadow: '0 0 0 3px rgba(24, 119, 242, 0.1)',
                                    },
                                },
                                '& .MuiOutlinedInput-root textarea': {
                                    fontSize: '0.95rem',
                                    color: '#1F2937',
                                },
                            }}
                        />
                    </Box>
                </Stack>
            </DialogContent>

            {/* Actions */}
            <DialogActions
                sx={{
                    p: 2.5,
                    px: 3,
                    backgroundColor: '#F5F5F5',
                    borderTop: '1px solid #E5E7EB',
                    gap: 1.5,
                }}
            >
                <Button
                    onClick={onClose}
                    variant="outlined"
                    startIcon={<CloseIcon sx={{ fontSize: 18 }} />}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        borderColor: '#D1D5DB',
                        color: '#6B7280',
                        '&:hover': {
                            borderColor: '#9CA3AF',
                            backgroundColor: '#F9F9F9',
                        },
                    }}
                >
                    Close
                </Button>
                <Button
                    onClick={handleSaveClick}
                    variant="contained"
                    disabled={!isFormValid}
                    startIcon={<SaveIcon sx={{ fontSize: 18 }} />}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        borderRadius: '8px',
                        background: isFormValid
                            ? 'linear-gradient(135deg, #1877F2 0%, #0A66C2 100%)'
                            : '#D1D5DB',
                        color: '#FFFFFF',
                        py: 1,
                        px: 3,
                        '&:hover': {
                            boxShadow: isFormValid ? '0 8px 20px rgba(24, 119, 242, 0.3)' : 'none',
                        },
                    }}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddSaleInvoicePrevFYModal;
